import { createSignalIfSupported, useApolloClient } from "@apollo/client";
import classNames from "classnames";
import hash from "hash-sum";
import { get, identity, isEmpty, pick, pickBy, unset } from "lodash-es";
import { formatUrl } from "next/dist/shared/lib/router/utils/format-url";
import { parseRelativeUrl } from "next/dist/shared/lib/router/utils/parse-relative-url";
import { useRouter } from "next/router";
import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { CommonHead, NoIndex } from "@components/CommonHead";
import CommonLayout from "@components/CommonLayout";
import { HorizontalNewsList } from "@components/HorizontalNewsList";
import Pager from "@components/Pager";
import { PoweredByArea } from "@components/PoweredByArea";
import { SearchInput } from "@components/SearchInput";
import { SelectedMatchLink } from "@components/SelectedMatchLink";
import { ShopCardList } from "@components/ShopCardList";
import Auth from "@hooks/useAuth";
import { CommonDataState } from "@hooks/useCommonData";
import { usePagination } from "@hooks/usePagination";
import type { SearchCondition } from "@hooks/useSearchCondition";
import { getSearchConditionFromSearchQueryParams } from "@hooks/useSearchCondition";
import type { TypeSearchQueryParams } from "@hooks/useSearchQueryParams";
import SearchQueryParams from "@hooks/useSearchQueryParams";
import { TeamsByTournamentState } from "@hooks/useTeamsByTournament";
import { isTypeProps, queryForSSP } from "@libs/apolloClient";
import { paths } from "@libs/paths";
import { getRouter as getRewriteRouter } from "@libs/rewrite";
import { useAbortPendingRequestOnUnmount } from "@libs/utils/abortFetch";
import {
  InvalidParametersError,
  NotFoundError,
} from "@libs/utils/dynamicURLParams/common";
import { getStaticRepresentation } from "@libs/utils/dynamicURLParams/shops";
import { GTMEvent, GTMEvents } from "@libs/utils/gtm";
import {
  getOgFromParsedUrlQuery,
  getOgFromSearchCondition,
} from "@libs/utils/og/shops";
import type { OgInfo } from "@libs/utils/staticURLParams/common";
import { getOgImagePathFromTournamentSlug } from "@libs/utils/staticURLParams/common";
import {
  isRecordNotFound,
  parseDynamicURLParameters,
} from "@libs/utils/staticURLParams/shops";
import {
  Shops_ShopsIndexPageDocument as ShopsIndexPageDocument,
  Shops_ShopsStaticUrlParamsDocument as ShopsStaticUrlParamsDocument,
} from "./__generated__/queries";
import styles from "./index.module.css";
import type {
  Shops_ShopsIndexPageQuery as ShopsIndexPageQuery,
  Shops_ShopsStaticUrlParamsQuery as ShopsStaticUrlParamsQuery,
} from "./__generated__/queries";
import type { ApolloError, ApolloQueryResult } from "@apollo/client";
import type { GetServerSideProps } from "next";
import type { ParsedUrlQuery } from "querystring";

const DEFAULT_PAGE_NUMBER = 1;
// PC版のショップカードのカレンダーに表示する日数
const SHOP_CARD_LARGE_CALENDAR_DAYS_AHEAD = 23;
const SHOP_CARD_LARGE_CALENDAR_DAYS_OFFSET = 0;

type ShopDetailProps = {
  parameters?: TypeSearchQueryParams;
  data?: ShopsIndexPageQuery;
  og: OgInfo;
  error?: ApolloError;
};

const filterOnlyAllowedQueryParams = (asPath: string): string => {
  const url = parseRelativeUrl(asPath);

  // Ignore other query params.
  url.query = pick(url.query, ["p"]);

  return formatUrl({
    pathname: url.pathname,
    query: url.query,
  });
};

const Index: React.FC<ShopDetailProps> = ({ data: initialData, og }) => {
  const [currentOg, setCurrentOg] = useState(og);
  const [response, setResponse] = useState(
    {} as ApolloQueryResult<ShopsIndexPageQuery | undefined>
  );

  const searchQueryParams = SearchQueryParams.useContainer();
  const { data: prefData } = CommonDataState.useContainer();

  const client = useApolloClient();
  const { asPath } = useRouter();
  const { fanstaUser } = Auth.useContainer();

  const rewriteRouter = getRewriteRouter();
  const result = rewriteRouter.match(asPath);
  const isRewrite = !!result;
  const isPageUrl = asPath === "/shops";
  const isStaticUrl = isRewrite;
  const isDynamicUrl = !isStaticUrl && !isPageUrl;

  const [searchCondition, setSearchCondition] =
    useState<SearchCondition | null>(null);

  const { allTournaments, allTeams } = TeamsByTournamentState.useContainer();

  const { currentPage, navigate } = usePagination();

  const selectedCombinedAreaIds = searchCondition?.combinedAreaIds || [];
  const selectedAreaIds = searchCondition?.areaIds || [];
  const selectedTeamIds = searchCondition?.teamIds || [];
  const matchId = searchCondition?.matchId || 0;
  const selectedTournamentIdsTba = searchCondition?.tournamentIdsTba || [];

  const prefectures = prefData?.masterPrefectures || [];
  const withNewsArticles = !!matchId || !isEmpty(selectedTeamIds);

  // Sync URL changes into SearchCondition.
  useEffect(() => {
    if (searchQueryParams.isReady) {
      const nextParams = getSearchConditionFromSearchQueryParams(
        searchQueryParams.params
      );
      setSearchCondition(nextParams);
    }
  }, [searchQueryParams.isReady]);

  // searchConditionが変わったらクエリを実行する。
  useEffect(() => {
    // SEE: [Cancellable apollo-client queries :: OpenHood](https://experiments.openhood.com/apollo-client/typescript/2020/10/23/apollo-client-cancellable-queries/)
    const { signal } = createSignalIfSupported();

    (async () => {
      if (!searchCondition || isEmpty(prefectures)) {
        return;
      }

      // Truthyな値のみを残す
      const _variables = pickBy(
        {
          matchId,
          combinedAreaIds: selectedCombinedAreaIds,
          areaIds: selectedAreaIds,
          page: currentPage,
          teamIds: selectedTeamIds,
          tournamentIdsTba: selectedTournamentIdsTba,
        },
        identity
      );
      const variables = {
        withNewsArticles,
        ..._variables,
        shopCardLargeCalendarDaysAhead: SHOP_CARD_LARGE_CALENDAR_DAYS_AHEAD,
        shopCardLargeCalendarDaysOffset: SHOP_CARD_LARGE_CALENDAR_DAYS_OFFSET,
      };

      const context = {
        debounceKey: "shops",
        // Authが有効だとクエリが遅くなるので、公開APIの場合は明示的にskipする。
        skipAuth: true,
        fetchOptions: {},
      };

      if (signal) {
        context.fetchOptions = { signal };
      }

      // クエリのタイミングを細かく制御するために、useQueryを使わない。
      const response = await client.query({
        query: ShopsIndexPageDocument,
        context,
        variables,
      });

      const { data } = response;

      const og = getOgFromSearchCondition(searchCondition, {
        ...data,
        masterPrefectures: prefectures,
        tournaments: allTournaments,
        teamsUnbundled: allTeams,
      });

      isDynamicUrl &&
        setCurrentOg({
          title: og[0],
          description: og[1],
          imagePath: null,
        });

      setResponse(response);
    })();
  }, [prefectures, asPath, hash(searchCondition)]);

  // Do abort pending request.
  useAbortPendingRequestOnUnmount();

  const data = response.data || initialData;
  const error = response.error;
  if (error) {
    console.error(error);
  }

  const searchResultItems = data?.searchShopsV2.items.nodes || [];

  const newsArticles = data?.newsArticles?.nodes || [];
  const canShowNews = withNewsArticles && !isEmpty(newsArticles);
  const hasMoreNews = data?.newsArticles?.hasNextPage;
  const moreNewsLinkPath = () => {
    if (!selectedTeamIds && !matchId) {
      return;
    }

    // 試合指定がある or 複数チームによる検索
    const allNewsPath = paths.newsArticles;
    if (matchId || selectedTeamIds.length > 1) {
      return allNewsPath;
    }

    const selectedTeamId = selectedTeamIds[0];
    const selectedTeam = allTeams.find((t) => t.id === selectedTeamId);
    if (
      !selectedTeam ||
      !selectedTeam.slug ||
      !selectedTeam.masterSportGenre?.slug
    ) {
      // ここにくることはない。きたらそれはバグである。
      console.error(`"${selectedTeamId}" is not found in allTeams.`);
      return;
    }

    return paths.teamNewsArticles(
      selectedTeam.masterSportGenre.slug,
      selectedTeam.slug
    );
  };

  const onClickNews = useCallback(
    (newsId: number) => {
      GTMEvent(
        GTMEvents.toNewsDetailPagePressed(
          "news_on_shops_page",
          newsId,
          fanstaUser?.currentUser.user?.favoriteTeams.map((ft) => ft.id)
        )
      );
    },
    [fanstaUser]
  );

  const onClickNewsMore = useCallback(() => {
    // 「もっと見る」押下時の挙動は以下の通り
    // - 1チームに絞り込んでいる場合、そのチームのニュース一覧に移動する
    // - それ以外の場合、全チームニュース一覧に移動する
    const teamId =
      selectedTeamIds?.length === 1 ? selectedTeamIds[0] : undefined;

    GTMEvent(GTMEvents.toNewsListPagePressed("news_on_shops_page", teamId));
  }, [selectedTeamIds]);

  const getCanonicalForDynamic = (): string => {
    if (prefData && data) {
      try {
        const staticRepresentation = getStaticRepresentation(
          prefData,
          data["match"],
          searchQueryParams.params
        );
        if (staticRepresentation !== null) {
          return staticRepresentation;
        }
      } catch (error) {
        const isTranslateStaticParamsError =
          error instanceof InvalidParametersError ||
          error instanceof NotFoundError;

        if (!isTranslateStaticParamsError) {
          throw error;
        }
      }
    }
    return "/shops";
  };

  const getCanonicalUrl = (): string => {
    if (isStaticUrl) {
      return filterOnlyAllowedQueryParams(asPath);
    }
    if (isPageUrl) {
      return "/shops";
    }
    return getCanonicalForDynamic();
  };
  const canonicalUrl = getCanonicalUrl();

  const isLoading = isEmpty(response) || response.loading;

  return (
    <CommonLayout footerPC>
      <CommonHead
        title={currentOg.title}
        description={currentOg.description}
        ogImage={currentOg.imagePath ?? undefined}
        ogUrl={canonicalUrl}
        options={{
          titleSuffix: true,
        }}
        canonicalUrl={canonicalUrl}
      />

      {/* we want to index dynamic urls that have a static canonical */}
      {isDynamicUrl && canonicalUrl === "/shops" && <NoIndex />}

      <div className={styles.main} data-test-id="shops-page">
        <div className={styles.inner}>
          <div className={styles.column}>
            <div className={styles.columnLeft}>
              <div className={styles.sticky}>
                <SearchInput
                  className={"sort"}
                  teamSearchPlaceholder="チーム"
                  onChange={(searchCondition): void => {
                    setSearchCondition(searchCondition);
                  }}
                  shouldSyncHistory
                  submitText="この条件で絞り込む"
                  isDisplayingSearchTitle
                />

                <div className={styles.link}>
                  <div
                    className={classNames(
                      "l-main__innerWidth",
                      styles.container
                    )}
                  >
                    <SelectedMatchLink match={data?.match ?? undefined} />
                  </div>
                </div>
              </div>
            </div>

            <div className={classNames(styles.columnRight)}>
              {!error && !isLoading && canShowNews && (
                <dl className={styles.newsArea}>
                  <dt className={styles.title}>関連ニュース</dt>
                  <dd className={styles.list}>
                    <HorizontalNewsList
                      newsArticles={newsArticles}
                      moreNewsLinkPath={moreNewsLinkPath()}
                      hasMoreButton={hasMoreNews}
                      onClickNews={onClickNews}
                      onClickMore={onClickNewsMore}
                    />
                  </dd>
                </dl>
              )}
              {!error && !isLoading && (
                <div
                  className={classNames("l-main__innerWidth", styles.container)}
                >
                  {isPageUrl || isStaticUrl ? (
                    <h1 className="c-contentTitle" data-test="header">
                      {currentOg.title}
                    </h1>
                  ) : (
                    <div className="none md:block">
                      <h1 className="c-contentTitle" data-test="header">
                        {currentOg.title}
                      </h1>
                    </div>
                  )}

                  <div className={styles.list}>
                    <ShopCardList items={searchResultItems} />
                  </div>

                  {searchResultItems.length > 0 && (
                    <Pager
                      onNavigation={navigate}
                      totalPage={data?.searchShopsV2.items.totalPage}
                      hasPreviousPage={
                        data?.searchShopsV2.items.hasPreviousPage
                      }
                      hasNextPage={data?.searchShopsV2.items.hasNextPage}
                      currentPage={currentPage}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="md:none">
          <PoweredByArea />
        </div>
      </div>
    </CommonLayout>
  );
};

export const getServerSideProps: GetServerSideProps = async (
  context /* SEE: https://nextjs.org/docs/api-reference/data-fetching/get-server-side-props#context-parameter */
) => {
  // 1. URLからクエリ文字列を取得
  const rewriteRouter = getRewriteRouter();
  const result = rewriteRouter.parseParams(context.resolvedUrl, context.query);
  const query = result?.params as ParsedUrlQuery;

  // tournamentSlugとteamSlug両方指定されている場合は古い仕様なので新しい仕様のURLにリダイレクトさせる
  if (query.tournamentSlug && query.teamSlug) {
    return {
      redirect: {
        statusCode: 301,
        destination: paths.staticShops({
          ...context.query, // prefix付きがよいのでcontext.queryを使っている
          tournamentSlug: "",
        }),
      },
    };
  }

  const searchArea = result?.terminatingParams?.area;
  const variables = {
    ...query,
    includeMasterPrefectures:
      searchArea === "prefecture" ||
      searchArea === "combinedArea" ||
      searchArea === "area",
    includeCombinedAreas:
      searchArea === "combinedArea" || searchArea === "area",
    includeAreas: searchArea === "area",
  };

  // 2. `StaticURLParamsQuery` を使って、クエリ文字列からパラメータ(ID)へ変換する
  const paramsQueryRes = await queryForSSP<
    ApolloQueryResult<ShopsStaticUrlParamsQuery>["data"]
  >({
    query: ShopsStaticUrlParamsDocument,
    context: {
      // Authが有効だとクエリが遅くなるので、公開APIの場合は明示的にskipする。
      skipAuth: true,
    },
    variables,
  });

  // 3. `DynamicURLParameters` を生成する。
  const data = get(paramsQueryRes, ["props", "data"], {});

  // SEE: https://nextjs.org/docs/api-reference/data-fetching/get-server-side-props#notfound
  if (isRecordNotFound(variables, data)) {
    return {
      notFound: true,
    };
  }

  const parsedDynamicURLParameters = parseDynamicURLParameters(data, query);
  // matchId is different from other static url parameters
  // because it is compatible with dynamic url parameters
  const shopsDynamicURLParameters = query.matchId
    ? {
        ...parsedDynamicURLParameters,
        match: Number(query.matchId),
      }
    : parsedDynamicURLParameters;

  const searchCondition = getSearchConditionFromSearchQueryParams(
    shopsDynamicURLParameters
  );

  // ページ設定
  const currentPage = Number(query.p || DEFAULT_PAGE_NUMBER);

  // 4. ページ固有のクエリを実行する
  const pageProps = await queryForSSP({
    query: ShopsIndexPageDocument,
    context: {
      // Authが有効だとクエリが遅くなるので、公開APIの場合は明示的にskipする。
      skipAuth: true,
    },
    variables: {
      ...searchCondition,
      // クライアントのレンダリング時に取得するのでSSR時はfalseにする
      withNewsArticles: false,
      page: currentPage,
      shopCardLargeCalendarDaysAhead: SHOP_CARD_LARGE_CALENDAR_DAYS_AHEAD,
      shopCardLargeCalendarDaysOffset: SHOP_CARD_LARGE_CALENDAR_DAYS_OFFSET,
    },
  });

  if (!isTypeProps(pageProps)) {
    return {
      props: {
        parameters: shopsDynamicURLParameters,
      },
    };
  }

  const pageData = pageProps.props.data as ShopsIndexPageQuery;
  const match = pageData?.match || undefined;

  const [title, description] = getOgFromParsedUrlQuery(query, data, match);

  const isRewrite = !!rewriteRouter.match(context.resolvedUrl);
  const isPageUrl = context.resolvedUrl === "/shops";
  const isStaticUrl = isRewrite;
  // because we assume URLs are static, the data returned from ShopsIndexPageQuery
  // is not useful when the URL is dynamic
  const isUsefulData = isStaticUrl || isPageUrl;
  if (!isUsefulData) {
    unset(pageProps, "props.data");
  }

  return {
    props: {
      // TODO: If the data page prop is unecessary remove it to avoid confusion.
      // See the matches page implementation as an example.
      ...pageProps.props,
      parameters: shopsDynamicURLParameters,
      og: {
        title,
        description,
        imagePath: getOgImagePathFromTournamentSlug(query.tournamentSlug),
      },
    },
  };
};

export default Index;
